import * as React from 'react';
import {ErrorAlert} from '../common/ErrorAlert';
import {Client, ClientWarningRequest, ErrorResponse, User} from '../api/dtos/dtos';
import {RouteComponentProps, withRouter} from 'react-router';
import * as paths from '../paths';
import furoApi from '../api/FuroApi';
import {Button, Switch, Tabs, Tag} from 'antd';
import locales from '../locales';
import ClientForm from './ClientForm';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import ClientContactsEditor from './ClientContactsEditor';
import ClientSiteEditor from './ClientSiteEditor';
import ButtonBar from '../common/ButtonBar';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {showDeleteConfirm, showSaveConfirm, showSaveSuccessNotification} from 'src/utils/dialogs';
import ClientStatisticsPage from "../statistics/ClientStatisticsPage";
import accountProvider from "../account/AccountProvider";

const {TabPane} = Tabs;
const TAB_SHEET = "sheet";
const TAB_STATISTICS = "statistics";
const TAB_RENTALS = "rentals";
const TAB_SUMMARIES = "summaries";
const TAB_CALENDAR = "calendar";

interface ClientDetailsPageState {
  editable: boolean;
  client?: Client;
  users: User[];
  error?: ErrorResponse;
}

class ClientDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, ClientDetailsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps) {
    super(props);

    this.state = {
      editable: false,
      users: [],
    };
  }

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {client, editable, error} = this.state;
    const isAuthenticated = accountProvider.isAdmin() || accountProvider.isSales();
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Tabs animated={false} onChange={this.onTabChange}>
          <TabPane tab={locales.sheet} key={TAB_SHEET}>
            {client && isAuthenticated && this.renderWarningToggle(client)}
            {isAuthenticated &&
                <>
                  {!client?.deleted &&
                      <ButtonBar style={{marginBottom: 24}}>
                          <Button
                              type="danger"
                              icon="delete"
                              onClick={this.onDeleteClick}
                          >
                            {locales.delete}
                          </Button>
                          <Button
                              type="primary"
                              icon="edit"
                              onClick={this.onEditClick}
                              disabled={editable}
                          >
                            {locales.edit}
                          </Button>
                      </ButtonBar>
                  }
                  {client?.deleted &&
                      <ButtonBar>
                          <Button
                              type="danger"
                              icon="delete"
                              onClick={this.onUndeleteClick}
                          >
                            {locales.undelete}
                          </Button>
                      </ButtonBar>
                  }
                </>

            }


            {client?.deleted &&
                <SectionHeaderFormItem title={
                  <Tag color="red">{locales.deleted}</Tag>
                }/>

            }

            <ClientForm
              client={client}
              editable={editable}
              users={this.state.users.map(user => user.userName!)}
              onFormSubmit={this.onFormSubmit}
              onFormCancel={this.onFormCancel}
            />

            {client && client.id && <>
                <SectionHeaderFormItem
                    title={locales.client_contacts}
                    style={{marginTop: 48}}
                >
                    <div style={{marginTop: -8}}><Tag color="blue">{locales.company}</Tag></div>
                </SectionHeaderFormItem>

                <ClientContactsEditor
                    clientId={client.id}
                    editable={!editable && isAuthenticated}
                />

                <SectionHeaderFormItem
                    title={locales.client_sites}
                    style={{marginTop: 16}}
                >
                    <div style={{marginTop: -8}}><Tag color="blue">{locales.company}</Tag></div>
                </SectionHeaderFormItem>

                <ClientSiteEditor
                    clientId={client.id}
                    editable={!editable && isAuthenticated}
                />
            </>}
          </TabPane>
          {client &&
              <TabPane tab={locales.statistics} key={TAB_STATISTICS}>
                  <ClientStatisticsPage client={client}/>
              </TabPane>
          }
          <TabPane tab={locales.rentals} key={TAB_RENTALS}/>
          <TabPane tab={locales.summaries} key={TAB_SUMMARIES}/>
          <TabPane tab={locales.calendar_events} key={TAB_CALENDAR}/>
        </Tabs>

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onTabChange = (activeKey: string) => {
    const {client} = this.state;
    if (client) {
      if (activeKey === TAB_RENTALS) {
        this.props.history.push(paths.viewClientRentalHistoryPath(client.id!), {appSubtitle: client.name});
      } else if (activeKey === TAB_SUMMARIES) {
        this.props.history.push(paths.viewClientSummaryPath(client.id!), {appSubtitle: client.name});
      } else if (activeKey === TAB_CALENDAR) {
        this.props.history.push(paths.viewClientCalendarPath(client.id!), {appSubtitle: client.name});
      }
    }
  };

  private reload = () => {
    this.loadClient(this.getIdParam()!);
    this.loadUsers();
  };

  private getIdParam = (): number => {
    const idParam = this.props.match.params.id;
    return Number(idParam)!;
  };

  private loadClient = (id: number) => {
    withLoadingOverlay(
      furoApi.clients.get(id)
        .then(this.onLoadClientSuccess)
        .catch(this.onApiError), this.loadingHandler);
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then(response => {
        this.setState({
          users: response,
          error: undefined,
        });
      })
      .catch((error: ErrorResponse) => {
        this.setState({error});
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadClientSuccess = (response: Client) => {
    this.setState({
      client: response,
      error: undefined,
    });

    setAppHeaderSubtitle(this.props, response.name);
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onFormSubmit = (client: Client) => {
    showSaveConfirm(() => this.saveFormValues(client));
  };

  private onFormCancel = () => {
    this.setState({
      editable: false,
    });
  };

  private onEditClick = () => {
    this.setState({
      editable: true,
    });
  };

  private onDeleteClick = () => {
    showDeleteConfirm(() => this.deleteClient(true));
  };


  private onUndeleteClick = () => {
    showDeleteConfirm(() => this.deleteClient(false), locales.undelete_confirm);
  };

  private deleteClient = (deleted: boolean) => {
    const {client} = this.state;

    if (!client) {
      return;
    }

    const request = {
      ...client,
      deleted,
    }

    withLoadingOverlay(
      furoApi.clients.save(request, this.getIdParam())
        .then(() => {
          showSaveSuccessNotification();
          this.reload();
        })
        .catch(this.onApiError), this.loadingHandler);
  }

  private saveFormValues = (client: Client) => {
    withLoadingOverlay(
      furoApi.clients.save(client, this.getIdParam())
        .then(this.onSaveClientSuccess)
        .catch(this.onApiError), this.loadingHandler);
  };

  private onSaveClientSuccess = () => {
    this.setState({
      error: undefined,
      editable: false,
    });
    showSaveSuccessNotification();
    this.reload();
  };

  private renderWarningToggle = (client: Client): React.ReactNode => {
    const onChange = (checked: boolean) => {
      // Note: api call + loading causes a lag in the switch's state change
      setTimeout(() => this.setClientWarning(checked), 300);
    };
    return (
      <ButtonBar>
        <p>{locales.warning}</p>
        <Switch
          size="small"
          defaultChecked={client.warning}
          onChange={onChange}
        />
      </ButtonBar>
    );
  };

  private setClientWarning = (warning: boolean) => {
    const request: ClientWarningRequest = {warning};
    const promise = furoApi.clients.setWarning(this.getIdParam(), request)
      .then(() => {
        showSaveSuccessNotification();
        this.reload();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

}

export default withRouter(ClientDetailsPage);




// WEBPACK FOOTER //
// ./src/client-details/ClientDetailsPage.tsx